/**
 * What the Skills section offers, worked out as data so the rows and the
 * picker only render it.
 */

import { type SkillSummary } from "@/shared/domain/skills";

import { isAgentEligibleSkill } from "../../Agents/skills/validation";
import { type AgentFormValues } from "../agentFormSchema";

type SkillSelection = AgentFormValues["skills"][number];

/** The org-shared skills an agent may attach, out of everything visible. */
export function eligibleAgentSkills(
  items: readonly SkillSummary[],
): SkillSummary[] {
  return items.filter(isAgentEligibleSkill);
}

/** Those of them the agent does not already carry. */
export function attachableSkills(
  eligible: readonly SkillSummary[],
  attached: readonly SkillSelection[],
): SkillSummary[] {
  const taken = new Set(attached.map((selection) => selection.skill_id));
  return eligible.filter((summary) => !taken.has(summary.skill.skill_id));
}

/** One option in a skill row's version picker. */
export interface SkillVersionOption {
  value: string;
  label: string;
}

/** The sentinel the picker reports for "whatever is newest at launch". */
export const LATEST_VERSION = "latest";

/**
 * The versions a row may be pinned to, newest first, under an entry that
 * pins nothing.
 *
 * The pinned version is always among them: until the fetch settles — or if
 * that version has since been deleted — it would otherwise be missing from
 * its own picker, which would silently read as "Latest".
 */
export function skillVersionOptions(
  pinned: number | null,
  latest: number | null,
  fetched: readonly number[],
): SkillVersionOption[] {
  const numbers = Array.from(
    new Set(pinned === null ? fetched : [...fetched, pinned]),
  ).sort((a, b) => b - a);
  return [
    {
      value: LATEST_VERSION,
      label: latest === null ? "Latest" : `Latest (v${latest})`,
    },
    ...numbers.map((version) => ({
      value: String(version),
      label: `v${version}`,
    })),
  ];
}
